import { MetricPeriod } from '@/lib/utils'

/**
 * Возвращает локальную дату в формате YYYY-MM-DD (без UTC-сдвигов).
 */
export function getLocalIsoDate(date: Date = new Date()): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Бизнес-дата в формате YYYY-MM-DD — московская, как public.ownership_business_date()
 * на сервере (там же RLS не даёт вносить дневные показатели позже этой даты).
 */
export function getMoscowIsoDate(date: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Moscow',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

/**
 * Границы недели в периоде
 */
export interface WeekBoundaries {
  weekStart: Date
  weekEnd: Date
  weekNumber: number
}

/**
 * Получает текущую неделю в периоде
 * @param period - Период для расчета
 * @returns Границы текущей недели
 */
export function getCurrentWeekInPeriod(period: MetricPeriod): WeekBoundaries {
  const today = new Date()
  const periodStart = new Date(period.period_start)
  const periodEnd = new Date(period.period_end)
  
  // Рассчитываем количество дней с начала периода
  const daysSincePeriodStart = Math.floor(
    (today.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)
  )
  
  // Определяем номер текущей недели (0-based)
  const currentWeekNumber = Math.floor(daysSincePeriodStart / 7)
  
  // Начало текущей недели
  const weekStart = new Date(periodStart)
  weekStart.setDate(periodStart.getDate() + currentWeekNumber * 7)
  
  // Теоретический конец недели (через 6 дней)
  const weekEndTheoretical = new Date(weekStart)
  weekEndTheoretical.setDate(weekStart.getDate() + 6)
  
  // Фактический конец недели (не позже сегодня и не позже конца периода)
  const weekEnd = weekEndTheoretical > today 
    ? today 
    : (weekEndTheoretical > periodEnd ? periodEnd : weekEndTheoretical)
  
  return { 
    weekStart, 
    weekEnd, 
    weekNumber: currentWeekNumber 
  }
}

/**
 * Фильтрует данные по диапазону дат
 * @param data - Массив данных с полем metric_date
 * @param startDate - Начальная дата
 * @param endDate - Конечная дата
 * @returns Отфильтрованный массив данных
 */
export function filterDataByDateRange<T extends { metric_date: string }>(
  data: T[],
  startDate: Date,
  endDate: Date
): T[] {
  const startStr = startDate.toISOString().split('T')[0]
  const endStr = endDate.toISOString().split('T')[0]
  
  return data.filter(item => {
    return item.metric_date >= startStr && item.metric_date <= endStr
  })
}

/**
 * Проверяет, является ли период текущим
 * @param period - Период для проверки
 * @returns true если сегодня внутри периода
 */
export function isCurrentPeriod(period: MetricPeriod): boolean {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const periodStart = new Date(period.period_start)
  periodStart.setHours(0, 0, 0, 0)
  
  const periodEnd = new Date(period.period_end)
  periodEnd.setHours(23, 59, 59, 999)
  
  return today >= periodStart && today <= periodEnd
}

/**
 * Получает предыдущий период из списка периодов
 * @param periods - Список всех периодов
 * @param currentPeriod - Текущий период
 * @returns Предыдущий период или null
 */
export function getPreviousPeriod(
  periods: MetricPeriod[], 
  currentPeriod: MetricPeriod
): MetricPeriod | null {
  // Находим индекс текущего периода
  const currentIndex = periods.findIndex(p => p.period_id === currentPeriod.period_id)
  
  // Возвращаем предыдущий период, если он существует
  if (currentIndex > 0) {
    return periods[currentIndex - 1]
  }
  
  return null
}

/**
 * Рассчитывает прогресс выполнения периода (0-100%)
 * @param period - Период
 * @returns Процент завершения периода
 */
export function calculatePeriodProgress(period: MetricPeriod): number {
  const today = new Date()
  const periodStart = new Date(period.period_start)
  const periodEnd = new Date(period.period_end)
  
  // Если период еще не начался
  if (today < periodStart) {
    return 0
  }
  
  // Если период уже закончился
  if (today > periodEnd) {
    return 100
  }
  
  // Рассчитываем процент завершения
  const totalDays = Math.floor(
    (periodEnd.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)
  ) + 1
  
  const elapsedDays = Math.floor(
    (today.getTime() - periodStart.getTime()) / (1000 * 60 * 60 * 24)
  ) + 1
  
  return Math.round((elapsedDays / totalDays) * 100)
}

/**
 * Получает дату N дней назад от начала недели
 * @param weekStart - Начало недели
 * @param daysBack - Количество дней назад
 * @returns Дата
 */
export function getDateBeforeWeek(weekStart: Date, daysBack: number = 1): Date {
  const date = new Date(weekStart)
  date.setDate(weekStart.getDate() - daysBack)
  return date
}

/**
 * Форматирует диапазон дат для отображения
 * @param startDate - Начальная дата
 * @param endDate - Конечная дата
 * @returns Строка с диапазоном дат
 */
export function formatDateRange(startDate: Date, endDate: Date): string {
  const options: Intl.DateTimeFormatOptions = { 
    day: 'numeric', 
    month: 'short' 
  }
  
  const start = startDate.toLocaleDateString('ru-RU', options)
  const end = endDate.toLocaleDateString('ru-RU', options)
  
  return `${start} - ${end}`
}
